<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('translations::title.translations') }}</h2>
        </v-col>
        <v-col cols="12">
            <div class="form-wrapper">
                <v-row class="grid">
                    <v-col cols="12" lg="3" class="filters">
                        <v-card class="w-100" elevation="3" style="border-radius: 16px">
                            <v-card-title>{{ t('translations::title.search') }}</v-card-title>
                            <v-card-text>
                                <v-form ref="filters">
                                    <v-select
                                        v-model="filters.language"
                                        :items="languages"
                                        :label="t('translations::filters.label.language')"
                                        append-icon="mdi-chevron-down"
                                        dense
                                        rounded
                                        outlined
                                        class="compact"
                                    >
                                    </v-select>
                                    <v-text-field
                                        v-model="filters.key"
                                        :label="t('translations::filters.label.key')"
                                        dense
                                        outlined
                                        rounded
                                        class="compact"
                                        @keyup.enter="applyFilters"
                                    >
                                    </v-text-field>
                                    <v-text-field
                                        v-model="filters.value"
                                        :label="t('translations::filters.label.value')"
                                        dense
                                        outlined
                                        rounded
                                        class="compact"
                                        @keyup.enter="applyFilters"
                                    >
                                    </v-text-field>
                                    <div class="d-flex justify-content-between align-items-center w-100 pt-5">
                                        <v-btn text color="primary" @click="resetFilters()">
                                            {{ t('filters::button.reset') }}
                                        </v-btn>
                                        <v-btn color="primary" class="mx-2" rounded @click="applyFilters()">
                                            {{ t('filters::button.filter') }}
                                        </v-btn>
                                    </div>
                                </v-form>
                            </v-card-text>
                        </v-card>
                        <v-card
                            class="w-100 mt-7"
                            elevation="3"
                            :loading="namespacesLoading"
                            style="border-radius: 16px"
                        >
                            <v-card-title>{{ t('translations::title.namespaces') }}</v-card-title>
                            <v-card-text>
                                <v-list>
                                    <v-list-item-group v-model="filtersNamespace" color="primary">
                                        <v-list-item
                                            v-for="namespace in namespaces"
                                            :key="namespace.key"
                                            link
                                            :class="calculateNamespaceClasses(namespace)"
                                        >
                                            <v-list-item-content>
                                                <v-list-item-title v-text="namespace.name"></v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list-item-group>
                                </v-list>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" lg="9" class="content">
                        <v-card class="w-100" elevation="3" :loading="loading" style="border-radius: 16px">
                            <v-card-title>{{ t('translations::title.translations') }}</v-card-title>
                            <v-card-text class="w-100">
                                <v-col cols="12" class="tabs">
                                    <v-tabs v-model="groupsTab" show-arrows>
                                        <v-tab
                                            v-for="(group, key) in translations"
                                            :key="key"
                                            :class="{
                                                highlight: group.match,
                                                'rounded-0': true,
                                                'text-align-left': true,
                                            }"
                                        >
                                            <div class="tab">
                                                {{ group.prefix }}
                                            </div>
                                        </v-tab>
                                        <v-tabs-items v-model="groupsTab">
                                            <v-tab-item v-for="(group, key) in translations" :key="key">
                                                <div class="my-5">
                                                    <div>
                                                        <ul class="translations-list">
                                                            <li v-for="field in group.items" :key="field.namespace_key">
                                                                <v-text-field
                                                                    v-if="field.placeholders.length"
                                                                    v-model="field.value"
                                                                    :label="field.key"
                                                                    dense
                                                                    outlined
                                                                    rounded
                                                                    background-color="#FFF"
                                                                    :class="{ highlight: field.match }"
                                                                    persistent-hint
                                                                    :rules="[
                                                                        (v) =>
                                                                            !!v ||
                                                                            t('default::validation.field.required'),
                                                                    ]"
                                                                    :hint="
                                                                        t('translations::hint.placeholders', {
                                                                            placeholders: field.placeholders.join(', '),
                                                                        })
                                                                    "
                                                                    @change="pushChange(field)"
                                                                ></v-text-field>
                                                                <v-text-field
                                                                    v-else
                                                                    v-model="field.value"
                                                                    :label="field.key"
                                                                    dense
                                                                    rounded
                                                                    :rules="[
                                                                        (v) =>
                                                                            !!v ||
                                                                            t('default::validation.field.required'),
                                                                    ]"
                                                                    outlined
                                                                    :class="{ highlight: field.match }"
                                                                    @change="pushChange(field)"
                                                                ></v-text-field>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </v-tab-item>
                                        </v-tabs-items>
                                    </v-tabs>
                                </v-col>
                                <v-card-actions class="justify-content-end p-3">
                                    <v-btn
                                        class="v-btn--contained ml-4"
                                        rounded
                                        text
                                        color="primary"
                                        @click="applyFilters()"
                                    >
                                        {{ t('default::button.discard_changes') }}
                                    </v-btn>
                                    <v-btn
                                        :disabled="!enableSave"
                                        :loading="loading"
                                        rounded
                                        color="primary"
                                        @click="storeTranslations"
                                    >
                                        {{ t('default::button.save') }}
                                    </v-btn>
                                </v-card-actions>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </v-col>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
    props: {
        language: {
            type: String,
            required: false,
            default: String,
        },
    },
    data() {
        return {
            filters: {
                namespace: 'default',
                language: this.language,
                key: null,
                value: null,
            },
            groupsTab: 0,
            translations: [],
            loading: false,
            namespacesLoading: false,
            changed: {},
            enableSave: false,
        };
    },
    computed: {
        ...mapState('translations', ['namespaces']),
        languages() {
            return this.$store.state.translations.languages.map((x) => ({ value: x.code, text: _.startCase(x.name) }));
        },
        filtersNamespace: {
            get() {
                return _.findIndex(this.namespaces, { key: this.filters.namespace });
            },
            set(key) {
                this.filters.namespace = this.namespaces[key].key;
            },
        },
    },
    watch: {
        'filters.namespace': function () {
            this.applyFilters();
        },
    },
    created() {
        this.loadNamespaces();
        this.loadLanguages();
        this.applyFilters();
    },
    methods: {
        ...mapActions('translations', ['loadNamespaces', 'loadLanguages', 'storeBulkTranslations']),
        applyFilters() {
            this.loading = true;
            this.enableSave = false;
            this.changed = {};

            axios.get(route('api.v1.translations.search', this.filters)).then((response) => {
                this.loading = false;
                this.translations = response.data;
            });

            this.loadNamespaces(this.filters);
        },

        resetFilters() {
            this.filters = {
                namespace: 'default',
                language: this.language,
                key: null,
                value: null,
            };

            this.applyFilters();
        },

        calculateNamespaceClasses(namespace) {
            let classes = {};

            if (namespace.key === this.filters.namespace) {
                classes.selected = true;
            }

            if (namespace.match) {
                classes.highlight = true;
            }

            return classes;
        },
        pushChange(field) {
            this.changed[field.namespace_key] = field.value;
            this.enableSave = true;
        },
        storeTranslations() {
            // console.log(this.changed);

            let data = [];

            _.each(this.changed, (value, key) => {
                data.push({
                    namespace_key: key,
                    value: value,
                    language: this.filters.language,
                });
            });
            this.storeBulkTranslations(data)
                .then(() => {
                    this.$root.notify('info', t('default::notification.saved'));
                })
                .catch(() => {
                    this.$root.notify('error', t('default::notification.not_saved'));
                });
        },
    },
};
</script>

<style scoped>
.v-list-item.highlight,
.v-tab.highlight {
    background: rgba(92, 187, 246, 0.25);
}

.highlight fieldset {
    background: #ccc;
    background: rgba(92, 187, 246, 0.25);
}

ul.translations-list {
    padding: 0;
    margin: 0;
}

ul.translations-list li {
    list-style-type: none;
}
</style>
